<script setup lang="ts">
/**
 * HeroSplit
 *
 * Primary hero component for service pages.
 * Layout: "Smart Wrap" Side-by-Side CTAs for Mobile.
 * Styling: Rounded-full buttons, distinct Metwide branding.
 */
// STRICT CONSTRAINT: Explicit imports for tree-shaking
import {
  ArrowRight,
  CheckCircle2,
  Clock,
  FileText,
  MapPin,
  Phone,
  ShieldCheck,
} from "lucide-vue-next";

import type { HeroSplitProps } from "~~/shared/types";

const { open: openIntentModal, resolveQuoteIntent, extractIntent } = useIntentModal();
const { trackCtaClick, trackPhoneClick } = useAnalytics();

interface Props extends HeroSplitProps {
  breadcrumbs?: Array<{ label: string; to?: string }>;
  formType?: "quick-enquiry" | "contact";
}

const props = withDefaults(defineProps<Props>(), {
  showForm: false,
  breadcrumbs: () => [],
  formType: "quick-enquiry",
});

const isResidential = computed(() =>
  (props.service ?? "").toLowerCase().includes("residential"),
);

/**
 * Page-level intent derived from the primary CTA's href (e.g. /contact?need=cctv-sydney
 * yields "cctv-sydney"). Used by both desktop button text fallback and the mobile
 * "Get a Quote" button so geo-specific lead context survives every CTA path.
 */
const pageIntent = computed(() => extractIntent(props.primaryCta?.href ?? "") ?? "");

const { resolveLink } = useServiceRoutes();

/**
 * Derive intent from CTA button text
 */
function deriveIntentFromButtonText(text: string): string {
  const lower = text.toLowerCase();
  if (lower.includes("consultation")) return "consultation";
  if (lower.includes("assessment")) return "assessment";
  if (lower.includes("support") || lower.includes("ticket")) return "support";
  if (lower.includes("quote")) return "free-quote";
  return "free-quote"; // Default fallback
}

/**
 * Check if CTA should open the intent modal based on text or href
 */
function shouldOpenModal(text: string, href?: string): boolean {
  const lower = text.toLowerCase();
  const textMatch =
    lower.includes("consultation") ||
    lower.includes("assessment") ||
    lower.includes("quote") ||
    lower.includes("support") ||
    lower.includes("ticket") ||
    lower.includes("get started");

  const hrefMatch =
    href &&
    (href.startsWith("/contact") || href.startsWith("/quote"));

  return textMatch || !!hrefMatch;
}

/**
 * Handle CTA click - open modal or navigate.
 * An explicit ?need= in the href takes priority so per-page intents (cctv-sydney,
 * residential-cctv-sydney, etc.) survive over the text-derived fallback.
 */
function handleCtaClick(text: string, href?: string) {
  trackCtaClick(text, 'hero_split', href);
  if (!shouldOpenModal(text, href)) return;
  const explicit = extractIntent(href ?? "");
  if (explicit) {
    openIntentModal(explicit);
    return;
  }
  openIntentModal(resolveQuoteIntent(deriveIntentFromButtonText(text), props.service));
}

/**
 * Handle the mobile-only Get-a-Quote button. Prefer the page's primaryCta intent,
 * fall back to the legacy resolveQuoteIntent('free-quote', service) shape.
 */
function handleMobileQuoteClick() {
  trackCtaClick('Get a Quote', 'hero_split_mobile', '/contact');
  if (pageIntent.value) {
    openIntentModal(pageIntent.value);
    return;
  }
  openIntentModal(resolveQuoteIntent('free-quote', props.service));
}

</script>

<template>
  <section class="relative min-h-150 bg-dark lg:min-h-175">
    <div class="absolute inset-0">
      <ResponsiveImage
        :src="image"
        :alt="title"
        class="size-full object-cover"
        loading="eager"
        fetchpriority="high"
        :width="1920"
        :height="1080"
      />
      <div
        class="absolute inset-0 bg-linear-to-r from-dark/95 via-dark/65 to-dark/30"
      ></div>
    </div>

    <div class="relative mx-auto max-w-7xl py-16 px-6 lg:px-8 lg:py-24">
      <div class="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
        <div class="max-w-xl">
          <nav v-if="breadcrumbs.length > 0" aria-label="Breadcrumb" class="mb-4">
            <ol
              class="flex flex-wrap items-center gap-1.5 text-xs uppercase tracking-widest"
            >
              <li
                v-for="(crumb, index) in breadcrumbs"
                :key="index"
                class="flex items-center gap-1.5"
              >
                <NuxtLink
                  v-if="crumb.to"
                  :to="crumb.to"
                  class="text-slate-400 transition-colors hover:text-white"
                >
                  {{ crumb.label }}
                </NuxtLink>
                <span v-else class="text-primary-400">{{ crumb.label }}</span>
                <span v-if="index < breadcrumbs.length - 1" class="text-slate-600"
                  >/</span
                >
              </li>
            </ol>
          </nav>

          <p
            v-if="trustLine"
            class="mb-4 flex items-center gap-2 text-sm font-medium text-primary-400"
          >
            <MapPin :size="16" />
            {{ trustLine }}
          </p>

          <h1
            class="mb-6 text-4xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl"
          >
            {{ title }}
          </h1>

          <p class="mb-8 text-lg leading-relaxed text-neutral-300 lg:text-xl">
            {{ description }}
          </p>

          <div v-if="!showForm" class="hidden lg:flex flex-wrap gap-4">
            <!-- Primary CTA: button for modal, NuxtLink for navigation -->
            <button
              v-if="primaryCta && shouldOpenModal(primaryCta.text, primaryCta.href)"
              type="button"
              class="inline-flex items-center gap-2 rounded-full bg-primary-400 px-8 py-4 font-bold text-white transition-all duration-300 hover:bg-primary-500 hover:shadow-lg"
              @click="handleCtaClick(primaryCta.text, primaryCta.href)"
            >
              {{ primaryCta.text }}
              <ArrowRight :size="20" />
            </button>
            <NuxtLink
              v-else-if="primaryCta"
              :to="resolveLink(primaryCta)"
              class="inline-flex items-center gap-2 rounded-full bg-primary-400 px-8 py-4 font-bold text-white transition-all duration-300 hover:bg-primary-500 hover:shadow-lg"
              @click="trackCtaClick(primaryCta!.text, 'hero_split', primaryCta!.href)"
            >
              {{ primaryCta.text }}
              <ArrowRight :size="20" />
            </NuxtLink>

            <!-- Secondary CTA: button for modal, NuxtLink for navigation -->
            <button
              v-if="secondaryCta && shouldOpenModal(secondaryCta.text, secondaryCta.href)"
              type="button"
              class="inline-flex items-center gap-2 rounded-full border-2 border-white/30 px-8 py-4 font-bold text-white transition-all duration-300 hover:border-white hover:bg-white/10"
              @click="handleCtaClick(secondaryCta.text, secondaryCta.href)"
            >
              {{ secondaryCta.text }}
            </button>
            <NuxtLink
              v-else-if="secondaryCta"
              :to="resolveLink(secondaryCta)"
              class="inline-flex items-center gap-2 rounded-full border-2 border-white/30 px-8 py-4 font-bold text-white transition-all duration-300 hover:border-white hover:bg-white/10"
              @click="trackCtaClick(secondaryCta!.text, 'hero_split', secondaryCta!.href)"
            >
              {{ secondaryCta.text }}
            </NuxtLink>
          </div>

          <div class="flex flex-wrap gap-3 lg:hidden w-full">
            <LocalPhone
              class="flex-1 min-w-35 flex items-center justify-center gap-2 px-4 py-3.5 rounded-full bg-yellow-500 hover:bg-yellow-400 text-black font-bold text-base transition-colors shadow-lg"
              @click="trackPhoneClick('hero_split_mobile')"
            >
              <template #default="{ display }">
                <Phone :size="18" />
                {{ display }}
              </template>
            </LocalPhone>

            <button
              type="button"
              class="flex-1 min-w-35 flex items-center justify-center gap-2 px-4 py-3.5 rounded-full bg-green-600 hover:bg-green-500 text-white font-bold text-base transition-colors shadow-lg"
              @click="handleMobileQuoteClick"
            >
              <FileText :size="18" />
              Get a Quote
            </button>
          </div>

          <div class="lg:hidden mt-6 space-y-2 px-1">
            <div class="flex items-center gap-3 text-sm text-neutral-200">
              <CheckCircle2 :size="16" class="text-green-500 shrink-0" />
              <span>Licensed & Certified</span>
            </div>
            <div class="flex items-center gap-3 text-sm text-neutral-200">
              <CheckCircle2 :size="16" class="text-green-500 shrink-0" />
              <span>Same Day Free Quote</span>
            </div>
          </div>

          <div v-if="!showForm" class="hidden lg:flex mt-12 items-center gap-8">
            <div class="flex items-center gap-2 text-sm text-neutral-400">
              <ShieldCheck :size="20" class="text-primary-400" />
              <span>Licensed & Insured</span>
            </div>
            <div class="flex items-center gap-2 text-sm text-neutral-400">
              <Clock :size="20" class="text-primary-400" />
              <span>Fast Response</span>
            </div>
          </div>
        </div>

        <div class="hidden lg:flex justify-end">
          <div v-if="showForm" class="w-full max-w-md">
            <div class="bg-white rounded-2xl shadow-2xl overflow-hidden">
              <!-- Contact Form (for contact page) -->
              <FormsContactForm
                v-if="formType === 'contact'"
                variant="hero"
                title="Send Us a Message"
                submit-text="Send Message"
              />
              <!-- Quick Enquiry Form (for service pages) -->
              <FormsQuickEnquiryForm
                v-else
                :service="service"
                variant="hero"
                title="Get a Free Quote"
                submit-text="Request Quote"
                :show-company="!isResidential"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
